import moment from 'moment';
import { camelCaseToSentenceCase } from '../../../../../shared/utils';
import { TimeArray } from '../../../Experiences/Owner/Property/TimeArray';
import { ServiceType, Status } from '../../../../enums/contractor';

export function compareProviderServiceTypes(
    a: ServiceType,
    b: ServiceType,
): number {
    const PROVIDER_SERVICE_TYPE_ORDER: ServiceType[] = [
        ServiceType.Catering,
        ServiceType.Alcohol,
        ServiceType.Wellness,
        ServiceType.Workshop,
        ServiceType.Other,
    ];
    const orderA = PROVIDER_SERVICE_TYPE_ORDER.indexOf(a);
    const orderB = PROVIDER_SERVICE_TYPE_ORDER.indexOf(b);

    return orderA - orderB;
}

export const OWNER_SET_MENU_CONTRACTOR_ID_CATERING = 'ownerSetMenuCatering';
export const OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL = 'ownerSetMenuAlcohol';

export function isJobCancelled(job: any): boolean {
    const s = String(job?.status ?? '')
        .trim()
        .toLowerCase();
    if (!s) return false;
    return (
        s === String(Status.Cancelled).toLowerCase() ||
        s === 'canceled' ||
        s === 'declined'
    );
}

export function jobDurationMinutes(
    startTime: string,
    endTime: string,
): number | null {
    if (!startTime || !endTime) return null;
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return null;
    const startM = sh * 60 + sm;
    const endM = eh * 60 + em;
    const diff = endM - startM;
    return diff > 0 ? diff : null;
}

export function formatServiceHours(totalMinutes: number): string {
    const h = totalMinutes / 60;
    const rounded = Math.round(h * 10) / 10;
    if (rounded <= 0) return '';
    if (rounded === 1) return '1 hr';
    return Number.isInteger(rounded)
        ? `${rounded} hrs`
        : `${rounded.toFixed(1)} hrs`;
}

export function timeValueToLabel(value: string): string {
    const t = TimeArray.find(
        (slot: { value: string; label: string }) => slot.value === value,
    );
    return t?.label ?? value;
}

export function getJobCardTitle(job: any, fallbackTitle: string): string {
    const firstItemName = job?.items?.[0]?.name;
    if (typeof firstItemName === 'string' && firstItemName.trim()) {
        return firstItemName.trim();
    }
    const firstService = job?.services?.[0];
    if (firstService) return camelCaseToSentenceCase(firstService);
    return fallbackTitle;
}

/** Total line-item quantity for job cards (menu / selected services). */
export function getJobItemsQuantityLabel(job: any): string {
    const items = Array.isArray(job?.items) ? job.items : [];
    const lineCount = items.length;
    const totalQty = items.reduce(
        (acc: number, item: any) =>
            acc + (Math.max(0, Number(item?.quantity)) || 0),
        0,
    );
    const setMenuName = job?.setMenu?.name;
    const quantity = job?.quantity;
    if (setMenuName) {
        return `${setMenuName} x ${quantity} ${quantity > 1 ? 'people' : 'person'}`;
    }
    if (lineCount === 0) {
        return 'No items selected';
    }
    if (totalQty === 0) {
        return `${lineCount} selection${lineCount !== 1 ? 's' : ''} · 0 items`;
    }
    if (lineCount > 1) {
        return `${totalQty} item${totalQty !== 1 ? 's' : ''} · ${lineCount} selections`;
    }
    return `${totalQty} item${totalQty !== 1 ? 's' : ''}`;
}

export function formatJobScheduleLine(
    jobDate: string,
    startTime: string,
    endTime: string,
): string {
    if (!jobDate && !startTime) return '';
    const datePart = jobDate
        ? moment(jobDate).format('MMM D, YYYY')
        : 'Date TBC';
    if (!startTime) return datePart;
    const startL = timeValueToLabel(startTime);
    const endL = endTime ? timeValueToLabel(endTime) : '';
    return endL
        ? `${datePart} • ${startL} — ${endL}`
        : `${datePart} • ${startL}`;
}
